/**
 * Componente EdicionItems - Tabla full width
 * Columnas: Código | Descripción | Cant | P.Unit | Subtotal | Producto | Deducible | Inventario | Bodega | X
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import Decimal from 'decimal.js';

const EdicionItems = ({
  items,
  onItemsChange,
  productos,
  cuentas,
  onBuscarProducto,
  cuentaDeducible,
  onCuentaDeducibleChange,
  cuentaInventario,
  onCuentaInventarioChange,
  bodegas = [],
}) => {
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [itemIndex, setItemIndex] = useState(null);
  const [tabProducto, setTabProducto] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [nuevoProducto, setNuevoProducto] = useState({
    codigo: '', codigo_auxiliar_sku: '', nombre: '', tipo: 'PRODUCTO',
    categoria: '', precio_1: '', precio_2: '', precio_3: '',
    aplica_iva: true, porcentaje_iva: 15,
  });

  const calcularSubtotal = (cantidad, precio) => {
    try { return new Decimal(cantidad || 0).times(new Decimal(precio || 0)).toFixed(2); }
    catch { return '0.00'; }
  };

  const handleEditarCampo = (index, campo, valor) => {
    const itemsActualizados = [...items];
    itemsActualizados[index] = { ...itemsActualizados[index], [campo]: valor };
    if (campo === 'cantidad' || campo === 'precio_unitario') {
      const cant = campo === 'cantidad' ? valor : itemsActualizados[index].cantidad;
      const precio = campo === 'precio_unitario' ? valor : itemsActualizados[index].precio_unitario;
      itemsActualizados[index].subtotal = calcularSubtotal(cant, precio);
    }
    onItemsChange(itemsActualizados);
  };

  const handleToggle = (index, campo) => {
    const itemsActualizados = [...items];
    itemsActualizados[index] = { ...itemsActualizados[index], [campo]: !itemsActualizados[index][campo] };
    onItemsChange(itemsActualizados);
  };

  const handleEliminarItem = (index) => onItemsChange(items.filter((_, i) => i !== index));

  const handleAgregarItem = () => {
    onItemsChange([...items, {
      codigo: '', descripcion: '', cantidad: 1, precio_unitario: 0, subtotal: '0.00',
      producto_id: '', producto_nombre: '', deducible: false, inventario: false, bodega: '',
    }]);
  };

  const handleAbrirDialogoProducto = (index) => {
    setItemIndex(index);
    setTabProducto(0);
    setBusqueda(items[index]?.descripcion || '');
    setNuevoProducto({
      codigo: items[index]?.codigo || '', codigo_auxiliar_sku: '',
      nombre: items[index]?.descripcion || '', tipo: 'PRODUCTO', categoria: '',
      precio_1: items[index]?.precio_unitario || '', precio_2: '', precio_3: '',
      aplica_iva: true, porcentaje_iva: 15,
    });
    if (items[index]?.descripcion) onBuscarProducto(items[index].descripcion);
    setDialogoAbierto(true);
  };

  const handleVincularProducto = (producto) => {
    if (itemIndex === null) return;
    const itemsActualizados = [...items];
    itemsActualizados[itemIndex] = {
      ...itemsActualizados[itemIndex],
      producto_id: producto.id, producto_nombre: producto.nombre,
      producto_codigo: producto.codigo, codigo: producto.codigo,
    };
    onItemsChange(itemsActualizados);
    setDialogoAbierto(false);
  };

  const handleCrearProducto = () => {
    if (!nuevoProducto.codigo || !nuevoProducto.nombre) { alert('Código y nombre son requeridos'); return; }
    if (itemIndex === null) return;
    const itemsActualizados = [...items];
    itemsActualizados[itemIndex] = {
      ...itemsActualizados[itemIndex],
      producto_id: `nuevo_${Date.now()}`, producto_nombre: nuevoProducto.nombre,
      producto_codigo: nuevoProducto.codigo, codigo: nuevoProducto.codigo,
      producto_nuevo: true, producto_data: { ...nuevoProducto },
    };
    onItemsChange(itemsActualizados);
    setDialogoAbierto(false);
  };

  const cuentasMovimiento = Array.isArray(cuentas) ? cuentas.filter((c) => c.acepta_movimiento) : [];

  const totalSubtotal = items.reduce((s, i) => { try { return new Decimal(s).plus(new Decimal(i.subtotal || 0)).toNumber(); } catch { return s; } }, 0);
  const totalDeducible = items.filter(i => i.deducible).reduce((s, i) => { try { return new Decimal(s).plus(new Decimal(i.subtotal || 0)).toNumber(); } catch { return s; } }, 0);
  const totalInventario = items.filter(i => i.inventario).reduce((s, i) => { try { return new Decimal(s).plus(new Decimal(i.subtotal || 0)).toNumber(); } catch { return s; } }, 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>Paso 3: Items de la Compra</Typography>

      <Alert severity="info" sx={{ mb: 1 }}>
        Marca "Deducible" para gastos deducibles. Marca "Inventario" para productos que van a stock.
      </Alert>

      <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAgregarItem} sx={{ mb: 1 }}>
        Agregar Item
      </Button>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#1a237e', '& th': { color: '#fff', fontWeight: 'bold', py: 1, fontSize: '0.75rem' } }}>
              <TableCell sx={{ width: 80 }}>Código</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell sx={{ width: 55 }} align="center">Cant</TableCell>
              <TableCell sx={{ width: 85 }} align="right">P.Unit</TableCell>
              <TableCell sx={{ width: 75 }} align="right">Subtotal</TableCell>
              <TableCell sx={{ width: 100 }} align="center">Producto</TableCell>
              <TableCell sx={{ width: 60 }} align="center">Deduc.</TableCell>
              <TableCell sx={{ width: 60 }} align="center">Invent.</TableCell>
              <TableCell sx={{ width: 100 }}>Bodega</TableCell>
              <TableCell sx={{ width: 35 }} align="center">X</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index} sx={{
                bgcolor: item.inventario ? '#e3f2fd' : item.deducible ? '#e8f5e9' : 'inherit',
                '& td': { py: 0.5, px: 0.5 }
              }}>
                <TableCell>
                  <TextField size="small" value={item.codigo || ''} variant="standard"
                    onChange={(e) => handleEditarCampo(index, 'codigo', e.target.value)}
                    inputProps={{ style: { fontSize: '0.8rem' } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={item.descripcion || ''} variant="standard"
                    onChange={(e) => handleEditarCampo(index, 'descripcion', e.target.value)}
                    inputProps={{ style: { fontSize: '0.8rem' } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={item.cantidad || ''} variant="standard"
                    onChange={(e) => handleEditarCampo(index, 'cantidad', e.target.value)}
                    inputProps={{ style: { fontSize: '0.8rem', width: 40, textAlign: 'center' } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={item.precio_unitario || ''} variant="standard"
                    onChange={(e) => handleEditarCampo(index, 'precio_unitario', e.target.value)}
                    inputProps={{ style: { fontSize: '0.8rem', width: 65 } }} />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold" fontSize="0.8rem">${item.subtotal || '0.00'}</Typography>
                </TableCell>
                <TableCell align="center">
                  {item.producto_id ? (
                    <Chip label={item.producto_nombre?.substring(0, 8) || '✓'} size="small"
                      color={item.producto_nuevo ? 'warning' : 'success'}
                      onDelete={() => handleEditarCampo(index, 'producto_id', '')} sx={{ fontSize: '0.65rem', height: 22 }} />
                  ) : (
                    <Button size="small" onClick={() => handleAbrirDialogoProducto(index)}
                      sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5 }}>
                      <LinkIcon fontSize="small" />
                    </Button>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Checkbox size="small" checked={!!item.deducible} onChange={() => handleToggle(index, 'deducible')} color="success" sx={{ p: 0 }} />
                </TableCell>
                <TableCell align="center">
                  <Checkbox size="small" checked={!!item.inventario} onChange={() => handleToggle(index, 'inventario')} color="primary" sx={{ p: 0 }} />
                </TableCell>
                <TableCell>
                  <Select size="small" value={item.bodega || ''} variant="standard" displayEmpty
                    onChange={(e) => handleEditarCampo(index, 'bodega', e.target.value)}
                    sx={{ fontSize: '0.75rem', minWidth: 80 }}>
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>--</em></MenuItem>
                    {bodegas.length > 0 ? (
                      bodegas.map(b => (
                        <MenuItem key={b.id} value={b.id} sx={{ fontSize: '0.75rem' }}>{b.nombre}</MenuItem>
                      ))
                    ) : (
                      <>
                        <MenuItem value="PRINCIPAL" sx={{ fontSize: '0.75rem' }}>Principal</MenuItem>
                        <MenuItem value="secundaria" sx={{ fontSize: '0.75rem' }}>Secundaria</MenuItem>
                      </>
                    )}
                  </Select>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" color="error" onClick={() => handleEliminarItem(index)} sx={{ p: 0 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Resumen y cuentas contables */}
      <Paper sx={{ p: 1.5, mt: 1, bgcolor: '#f5f5f5' }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="body2"><strong>Subtotal:</strong> ${new Decimal(totalSubtotal).toFixed(2)}</Typography>
            <Typography variant="body2" color="success.main"><strong>Deducible:</strong> ${new Decimal(totalDeducible).toFixed(2)}</Typography>
            <Typography variant="body2" color="primary"><strong>Inventario:</strong> ${new Decimal(totalInventario).toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={12} md={4.5}>
            {totalDeducible > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Cuenta Deducibles</InputLabel>
                <Select value={cuentaDeducible || ''} label="Cuenta Deducibles"
                  onChange={(e) => onCuentaDeducibleChange(e.target.value)} sx={{ fontSize: '0.8rem' }}>
                  {cuentasMovimiento.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>{c.codigo} - {c.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} md={4.5}>
            {totalInventario > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Cuenta Inventario</InputLabel>
                <Select value={cuentaInventario || ''} label="Cuenta Inventario"
                  onChange={(e) => onCuentaInventarioChange(e.target.value)} sx={{ fontSize: '0.8rem' }}>
                  {cuentasMovimiento.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>{c.codigo} - {c.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Diálogo Vincular/Crear Producto */}
      <Dialog open={dialogoAbierto} onClose={() => setDialogoAbierto(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vincular o Crear Producto</DialogTitle>
        <DialogContent>
          <Tabs value={tabProducto} onChange={(_, v) => setTabProducto(v)} sx={{ mb: 2 }}>
            <Tab label="Buscar Existente" icon={<SearchIcon />} iconPosition="start" />
            <Tab label="Crear Nuevo" icon={<AddIcon />} iconPosition="start" />
          </Tabs>

          {tabProducto === 0 && (
            <Box>
              <TextField fullWidth label="Buscar producto" value={busqueda}
                onChange={(e) => { setBusqueda(e.target.value); onBuscarProducto(e.target.value); }} sx={{ mb: 2 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
              {productos.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>Código</TableCell><TableCell>Nombre</TableCell>
                      <TableCell>Tipo</TableCell><TableCell align="right">Precio</TableCell><TableCell>Acción</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {productos.map((prod) => (
                        <TableRow key={prod.id} hover>
                          <TableCell>{prod.codigo}</TableCell>
                          <TableCell>{prod.nombre}</TableCell>
                          <TableCell><Chip label={prod.tipo} size="small" color={prod.tipo === 'PRODUCTO' ? 'primary' : 'secondary'} /></TableCell>
                          <TableCell align="right">${parseFloat(prod.precio_1 || 0).toFixed(2)}</TableCell>
                          <TableCell><Button size="small" variant="contained" onClick={() => handleVincularProducto(prod)}>Seleccionar</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Alert severity="info">No se encontraron. Crea uno nuevo.</Alert>}
            </Box>
          )}

          {tabProducto === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}><Alert severity="info" sx={{ mb: 1 }}>Se guardará al registrar la compra.</Alert></Grid>
              <Grid item xs={4}><TextField fullWidth required label="Código" value={nuevoProducto.codigo} onChange={(e) => setNuevoProducto({ ...nuevoProducto, codigo: e.target.value })} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="SKU" value={nuevoProducto.codigo_auxiliar_sku} onChange={(e) => setNuevoProducto({ ...nuevoProducto, codigo_auxiliar_sku: e.target.value })} /></Grid>
              <Grid item xs={4}>
                <FormControl fullWidth><InputLabel>Tipo</InputLabel>
                  <Select value={nuevoProducto.tipo} label="Tipo" onChange={(e) => setNuevoProducto({ ...nuevoProducto, tipo: e.target.value })}>
                    <MenuItem value="PRODUCTO">Producto</MenuItem><MenuItem value="SERVICIO">Servicio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField fullWidth required label="Nombre" value={nuevoProducto.nombre} onChange={(e) => setNuevoProducto({ ...nuevoProducto, nombre: e.target.value })} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="PVP" type="number" value={nuevoProducto.precio_1} onChange={(e) => setNuevoProducto({ ...nuevoProducto, precio_1: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Mayorista" type="number" value={nuevoProducto.precio_2} onChange={(e) => setNuevoProducto({ ...nuevoProducto, precio_2: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Especial" type="number" value={nuevoProducto.precio_3} onChange={(e) => setNuevoProducto({ ...nuevoProducto, precio_3: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
              <Grid item xs={6}>
                <FormControl fullWidth><InputLabel>IVA</InputLabel>
                  <Select value={nuevoProducto.aplica_iva ? 'si' : 'no'} label="IVA" onChange={(e) => setNuevoProducto({ ...nuevoProducto, aplica_iva: e.target.value === 'si' })}>
                    <MenuItem value="si">Sí (15%)</MenuItem><MenuItem value="no">No (0%)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}><TextField fullWidth label="Categoría" value={nuevoProducto.categoria} onChange={(e) => setNuevoProducto({ ...nuevoProducto, categoria: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoAbierto(false)}>Cancelar</Button>
          {tabProducto === 1 && <Button variant="contained" color="success" onClick={handleCrearProducto}>Crear y Vincular</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EdicionItems;
